import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { PhoneIcon } from './Icons';
import SelectMenu from './SelectMenu';

// Lets an admin pick one of the known agents and ring that agent + the
// customer together via Tata's click-to-call API (server/tataClient.js).
function CallButton({ agents, customerNumber, onCallTriggered }) {
  const { authFetch, user } = useAuth();
  // Defaults to whichever agent this login is linked to (see Manage Admins),
  // so the picker doesn't need to be changed manually every time. Falls back
  // to the first agent if this login has no link or that agent isn't in the list.
  const linkedAgent = agents.find((agent) => agent.number === user?.agentNumber);
  const [agentNumber, setAgentNumber] = useState(linkedAgent?.number || agents[0]?.number || '');
  const [status, setStatus] = useState('idle'); // idle | calling | done | error
  const [errorMessage, setErrorMessage] = useState('');

  if (agents.length === 0) return <span>-</span>;

  const handleCall = async () => {
    if (!agentNumber) return;
    setStatus('calling');
    setErrorMessage('');
    try {
      const response = await authFetch('/api/click-to-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentNumber, destinationNumber: customerNumber }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Call failed');
      setStatus('done');
      // Which placement this was (see POST /api/click-to-call) — the
      // call-history modal sends it with a note for this call.
      onCallTriggered({ agentNumber, placedAt: data.placedAt });
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message);
    }
  };

  return (
    <div className="click-to-call">
      <SelectMenu
        value={agentNumber}
        onChange={(value) => {
          setAgentNumber(value);
          setStatus('idle');
        }}
        disabled={status === 'calling'}
        ariaLabel="Call as"
        options={agents.map((agent) => ({ value: agent.number, label: agent.name }))}
      />
      <button
        className="btn btn-call"
        onClick={handleCall}
        disabled={status === 'calling'}
        title={`Call ${customerNumber} via ${agentNumber}`}
      >
        <PhoneIcon width={14} height={14} />
        {status === 'calling' ? 'Calling…' : 'Call'}
      </button>
      {status === 'done' && <span className="call-feedback call-feedback-ok">Ringing</span>}
      {status === 'error' && <span className="call-feedback call-feedback-error">{errorMessage}</span>}
    </div>
  );
}

export default CallButton;
